import styled from "@emotion/styled";
import React, { useState } from "react";
import { useModal } from "@context/ModalContext";
import { Store } from "@apis/index";
import { Helper } from "@utils/index";
export type FilterState = {
  paymentStatus: string;
  datePreset: string;
  startDate: string;
  endDate: string;
  storeId?: string;
  customerId?: string;
  deliveryBoyId?: string;
};

const FilterSidebar = ({
  initialFilters,
  onApply,
}: {
  initialFilters: FilterState;
  onApply: (newFilters: FilterState) => void;
}) => {
  const { hideModal } = useModal();
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [stores, setStores] = useState<any[]>([]);
  const user = Helper.getUser();

  React.useEffect(() => {
    if (user?.role === "admin") {
      const fetchStores = async () => {
        try {
          const res = await Store.list({ params: { limit: 1000 } });
          setStores(res?.items || []);
        } catch (error) {
          console.error("Failed to fetch stores", error);
        }
      };
      fetchStores();
    }
  }, []);

  const handleApply = () => {
    onApply(filters);
    hideModal();
  };

  const handleClearAll = () => {
    setFilters({
      paymentStatus: "",
      datePreset: "",
      startDate: "",
      endDate: "",
      storeId: initialFilters.storeId || "",
      customerId: initialFilters.customerId,
      deliveryBoyId: initialFilters.deliveryBoyId,
    });
  };

  const handleStoreChange = (storeId: string) => {
    setFilters({ ...filters, storeId });
  };

  const handleDatePresetChange = (preset: string) => {
    const today = new Date();
    let start = "";
    let end = "";

    if (preset === "Today") {
      start = today.toISOString().split("T")[0];
      end = today.toISOString().split("T")[0];
    } else if (preset === "This Week") {
      const first = today.getDate() - today.getDay();
      const last = first + 6;
      const firstDay = new Date(today.setDate(first));
      const lastDay = new Date(today.setDate(last));
      start = firstDay.toISOString().split("T")[0];
      end = lastDay.toISOString().split("T")[0];
    } else if (preset === "This Month") {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      start = firstDay.toISOString().split("T")[0];
      end = lastDay.toISOString().split("T")[0];
    }

    setFilters({
      ...filters,
      datePreset: preset,
      startDate: start,
      endDate: end,
    });
  };

  return (
    <SidebarContainer>
      <SidebarHeader>
        <HeaderTitle>
          <IconWrapper>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 6H20M4 12H14M4 18H10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </IconWrapper>
          Filters
        </HeaderTitle>
      </SidebarHeader>

      <ScrollableContent>
        <FilterSection>
          <SectionHeader>
            <SectionTitle>Date Range</SectionTitle>
            <SectionSubtitle>Filter orders by time period</SectionSubtitle>
          </SectionHeader>
          <FormGroup>
            <Label>Time Period</Label>
            <SelectWrapper>
              <StyledSelect
                value={filters.datePreset}
                onChange={(e) => handleDatePresetChange(e.target.value)}
              >
                <option value="">All Time</option>
                <option value="Today">Today</option>
                <option value="This Week">This Week</option>
                <option value="This Month">This Month</option>
                <option value="Custom">Custom Range</option>
              </StyledSelect>
              <SelectIcon>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </SelectIcon>
            </SelectWrapper>
          </FormGroup>

          {filters.datePreset === "Custom" && (
            <DateInputsGrid>
              <FormGroup>
                <Label>Start Date</Label>
                <StyledInput
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters({ ...filters, startDate: e.target.value })
                  }
                />
              </FormGroup>
              <FormGroup>
                <Label>End Date</Label>
                <StyledInput
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters({ ...filters, endDate: e.target.value })
                  }
                />
              </FormGroup>
            </DateInputsGrid>
          )}
        </FilterSection>

        <Separator />

        {user?.role === "admin" && (
          <>
            <FilterSection>
              <SectionHeader>
                <SectionTitle>Store</SectionTitle>
                <SectionSubtitle>Filter orders by store</SectionSubtitle>
              </SectionHeader>
              <FormGroup>
                <SelectWrapper>
                  <StyledSelect
                    value={filters.storeId || ""}
                    onChange={(e) => handleStoreChange(e.target.value)}
                  >
                    <option value="">All Stores</option>
                    {stores.map((store) => (
                      <option key={store.id} value={store.id}>
                        {store.storeName || store.name}
                      </option>
                    ))}
                  </StyledSelect>
                  <SelectIcon>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </SelectIcon>
                </SelectWrapper>
              </FormGroup>
            </FilterSection>
            <Separator />
          </>
        )}

        <FilterSection>
          <SectionHeader>
            <SectionTitle>Payment Status</SectionTitle>
            <SectionSubtitle>Filter by payment status</SectionSubtitle>
          </SectionHeader>
          <RadioGroup>
            {["All", "Paid", "Unpaid"].map((status) => (
              <RadioLabel key={status}>
                <HiddenRadio
                  type="radio"
                  name="paymentStatus"
                  value={status}
                  checked={
                    status === "All"
                      ? !filters.paymentStatus || filters.paymentStatus === ""
                      : filters.paymentStatus === status
                  }
                  onChange={() =>
                    setFilters({
                      ...filters,
                      paymentStatus: status === "All" ? "" : status,
                    })
                  }
                />
                <StyledRadio
                  checked={
                    status === "All"
                      ? !filters.paymentStatus || filters.paymentStatus === ""
                      : filters.paymentStatus === status
                  }
                />
                <RadioText>{status}</RadioText>
              </RadioLabel>
            ))}
          </RadioGroup>
        </FilterSection>
      </ScrollableContent>

      <SidebarFooter>
        <ClearButton onClick={handleClearAll}>Clear All</ClearButton>
        <ApplyButton onClick={handleApply}>
          Apply Filters
          <ButtonIcon>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 12H19M19 12L15 8M19 12L15 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ButtonIcon>
        </ApplyButton>
      </SidebarFooter>
    </SidebarContainer>
  );
};

// Styled Components

const SidebarContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  padding: 24px 32px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  flex-shrink: 0;
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 12px;
  letter-spacing: -0.025em;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  background: #eff6ff;
  padding: 8px;
  border-radius: 10px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #64748b;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #cbd5e0;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.01em;
`;

const SectionSubtitle = styled.span`
  font-size: 13px;
  color: #64748b;
`;

const Separator = styled.div`
  height: 1px;
  background: #f1f5f9;
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #475569;
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  padding-right: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  cursor: pointer;
  appearance: none;
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover {
    border-color: #cbd5e0;
    background: #f1f5f9;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SelectIcon = styled.div`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #64748b;
  display: flex;
  align-items: center;
`;

const DateInputsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: flex-end;
`;

const StyledInput = styled.input`
  width: 90%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  transition: all 0.2s ease;
  font-weight: 500;
  font-family: inherit;

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
    transition: 0.2s;
  }
  &::-webkit-calendar-picker-indicator:hover {
    opacity: 1;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.2s ease;
  user-select: none;
  border: 1px solid transparent;

  &:hover {
    background: #f8fafc;
    border-color: #f1f5f9;
  }
`;

const HiddenCheckbox = styled.input`
  display: none;
`;

const StyledCheckbox = styled.div<{ checked: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${({ checked }) => (checked ? "#3b82f6" : "#cbd5e0")};
  background: ${({ checked }) => (checked ? "#3b82f6" : "white")};
  border-radius: 6px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 14px;
    height: 14px;
    visibility: ${({ checked }) => (checked ? "visible" : "hidden")};
    transform: ${({ checked }) => (checked ? "scale(1)" : "scale(0)")};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const CheckboxText = styled.span`
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #334155;
`;

const CountBadge = styled.span`
  background: #f1f5f9;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  min-width: 24px;
  text-align: center;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 6px 0;
  user-select: none;

  &:hover span {
    color: #1e293b;
  }
`;

const HiddenRadio = styled.input`
  display: none;
`;

const StyledRadio = styled.div<{ checked: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${({ checked }) => (checked ? "#3b82f6" : "#cbd5e0")};
  border-radius: 50%;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%)
      scale(${({ checked }) => (checked ? 1 : 0)});
    width: 10px;
    height: 10px;
    background: #3b82f6;
    border-radius: 50%;
    transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const RadioText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  transition: color 0.2s;
`;

const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Chip = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid ${({ active }) => (active ? "#3b82f6" : "#e2e8f0")};
  background: ${({ active }) => (active ? "#eff6ff" : "white")};
  color: ${({ active }) => (active ? "#2563eb" : "#64748b")};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${({ active }) =>
    active ? "0 2px 4px rgba(59, 130, 246, 0.1)" : "none"};

  &:hover {
    background: ${({ active }) => (active ? "#dbeafe" : "#f8fafc")};
    border-color: ${({ active }) => (active ? "#2563eb" : "#cbd5e0")};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SidebarFooter = styled.div`
  padding: 24px 32px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  gap: 16px;
  background: white;
  margin-top: auto;
  flex-shrink: 0; /* Prevent shrinking */
`;

const ClearButton = styled.button`
  flex: 1;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e0;
    color: #1e293b;
  }
`;

const ApplyButton = styled.button`
  flex: 1.5;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: #3b82f6;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s;
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.2);

  &:hover {
    background: #2563eb;
    box-shadow: 0 6px 8px -1px rgba(59, 130, 246, 0.3);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ButtonIcon = styled.span`
  display: flex;
  align-items: center;
`;

export default FilterSidebar;
